import { useEffect, useMemo, useRef, useState } from "react";
import Shell from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Search, Users, LayoutGrid, FlaskConical, ShieldCheck, Store, Star } from "lucide-react";
import { countryName, countryNames } from "@/lib/countries";

const TABS = [
  ["explore", "Explore", LayoutGrid],
  ["directory", "Developer directory", Users],
  ["sell", "Sell", Store],
  ["creator", "Creator dashboard", ShieldCheck],
  ["sandbox", "Regulatory sandbox", FlaskConical],
];

function Badges({ badges }) {
  if (!badges) return null;
  return (
    <div style={{ marginTop: 8 }} data-testid="listing-badges">
      <span className="k-badge k-badge-accent">{badges.automated_checks}</span>
      {badges.verified_on && <span className="k-muted" style={{ fontSize: 11, marginLeft: 8 }}>verified on {String(badges.verified_on).slice(0, 10)}</span>}
      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {(badges.compliance_modules || []).map((m) => (
          <span key={m.id} className="k-badge k-badge-sandbox" title={m.status}>{m.id}: {m.status}</span>
        ))}
      </div>
    </div>
  );
}

function ExploreView() {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [sector, setSector] = useState("");
  const [priv, setPriv] = useState(false);
  const [rows, setRows] = useState([]);
  const timer = useRef(null);

  const load = async () => {
    try {
      const params = {};
      if (region) params.region = region;
      if (sector) params.sector = sector;
      if (priv) params.enterprise_private = true;
      const { data } = await api.get("/store/listings", { params });
      setRows(data.listings || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region, sector, priv]);
  // debounced local keyword filter (no per-keystroke server/LLM call)
  useEffect(() => { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => {}, 250); }, [q]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => (r.title + " " + (r.summary || "")).toLowerCase().includes(t));
  }, [q, rows]);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
          <Search size={15} style={{ position: "absolute", left: 10, top: 11, opacity: 0.6 }} />
          <input className="k-control" data-testid="marketplace-search" placeholder="Search listings…"
            value={q} onChange={(e) => setQ(e.target.value)} style={{ paddingLeft: 32 }} />
        </div>
        <input className="k-control" data-testid="filter-region" placeholder="Region code (e.g. UK)"
          value={region} onChange={(e) => setRegion(e.target.value.toUpperCase())} style={{ maxWidth: 180 }} />
        <input className="k-control" data-testid="filter-sector" placeholder="Sector (e.g. FIN)"
          value={sector} onChange={(e) => setSector(e.target.value.toUpperCase())} style={{ maxWidth: 160 }} />
        <button className={`k-btn ${priv ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="toggle-private"
          onClick={() => setPriv((v) => !v)}>{priv ? "Enterprise private" : "Public marketplace"}</button>
      </div>
      {filtered.length === 0 && <p className="k-muted" data-testid="no-listings">No listings yet.</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
        {filtered.map((r) => (
          <div key={r.id} className="k-container" data-testid={`listing-${r.id}`} style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <h3 className="k-heading" style={{ margin: 0, fontSize: 16 }}>{r.title}</h3>
                {r.featured && <span className="k-badge k-badge-featured" data-testid={`listing-featured-${r.id}`}><Star size={11} /> Featured</span>}
              </div>
              <span className="k-badge">{r.licensing_type?.replace(/_/g, " ").toLowerCase()}</span>
            </div>
            <p className="k-muted" style={{ fontSize: 13 }}>{r.summary}</p>
            <p className="k-muted" style={{ fontSize: 12 }}>Region: {r.primary_jurisdiction || "—"} · fork: {r.fork_behavior}</p>
            <Badges badges={r.badges} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DirectoryView() {
  const [devs, setDevs] = useState([]);
  useEffect(() => { api.get("/directory/").then((r) => setDevs(r.data.developers || [])).catch(() => {}); }, []);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
      {devs.length === 0 && <p className="k-muted" data-testid="no-devs">No developer profiles yet.</p>}
      {devs.map((d) => (
        <div key={d.user_id} className="k-container" data-testid={`dev-${d.user_id}`} style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h3 className="k-heading" style={{ margin: 0, fontSize: 16 }}>{d.display_name}</h3>
            <span className="k-badge k-badge-accent">{d.badge_status}</span>
          </div>
          <p className="k-muted" style={{ fontSize: 12 }}>Reputation: {d.reputation_status?.score ?? 0} · completed {d.reputation_status?.completed_contracts ?? 0}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
            {(d.jurisdiction_specializations || []).slice(0, 8).map((j) => <span key={j} className="k-badge k-badge-sandbox">{countryName(j)}</span>)}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
            {(d.api_mastery_tags || []).slice(0, 8).map((t) => <span key={t} className="k-badge">{t}</span>)}
          </div>
          <button className="k-btn k-btn-ghost" data-testid={`hire-${d.user_id}`} style={{ marginTop: 10 }}
            onClick={() => toast.info("Open a project, then start a scoping brief from the hiring wizard.")}>Request a custom build</button>
        </div>
      ))}
    </div>
  );
}

function CreatorDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  useEffect(() => { if (user?.id) api.get(`/directory/profile/${user.id}`).then((r) => setProfile(r.data)).catch(() => setProfile(false)); }, [user]);
  if (profile === false) return <p className="k-muted">Create a directory profile to see your dashboard.</p>;
  if (!profile) return <p className="k-muted">Loading…</p>;
  return (
    <div className="k-container" style={{ padding: 18 }} data-testid="creator-dashboard">
      <h3 className="k-heading" style={{ marginTop: 0 }}>{profile.display_name}</h3>
      <p className="k-muted">Badge: {profile.badge_status} · Connect onboarding: {profile.connect_onboarding}</p>
      <p className="k-muted">Reputation score: {profile.reputation_status?.score} · Fee tier: {profile.platform_fee_tier?.tier} ({(profile.platform_fee_tier?.kadana_bps || 0) / 100}% Kadana, illustrative)</p>
      <p className="k-muted" style={{ fontSize: 12 }}>Funds in progress appear here once the licensed escrow flow is enabled. Kadana holds no funds today.</p>
    </div>
  );
}

function RegulatorySandbox() {
  const [conns, setConns] = useState([]);
  useEffect(() => { api.get("/catalogue/search", { params: { kind: "connector" } }).then((r) => setConns([...(r.data.catalogue || []), ...(r.data.registry || [])].slice(0, 40))).catch(() => {}); }, []);
  return (
    <div>
      <div className="k-container" style={{ padding: 14, marginBottom: 14 }}>
        <span className="k-badge k-badge-sandbox">SANDBOX</span>
        <p className="k-muted" style={{ fontSize: 12, marginTop: 8 }}>
          Simulated regional connector environments (mock keys, simulated webhooks). Sandbox testing verifies
          technical formatting only and is <b>not a legal or regulatory certification</b> by any authority.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {conns.map((c) => (
          <div key={c.id} className="k-container" data-testid={`sandbox-${c.id}`} style={{ padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <strong style={{ fontSize: 14 }}>{c.name}</strong>
              <span className="k-badge k-badge-sandbox">SANDBOX</span>
            </div>
            <p className="k-muted" style={{ fontSize: 12 }}>{c.id} · {countryNames(c.jur || c.jurisdictions || [])}</p>
            <button className="k-btn k-btn-ghost" data-testid={`sim-${c.id}`} style={{ marginTop: 8 }}
              onClick={() => toast.success(`Simulated ${c.name}: mock request accepted (formatting only)`)}>Run simulated call</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SellView() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ title: "", summary: "", sector: "FIN", primary_jurisdiction: "UK",
    licensing_type: "ONE_TIME_PURCHASE", fork_behavior: "FULL_SOURCE_EXPORT", price_minor: 4900 });
  const loadP = () => { if (user?.id) api.get(`/directory/profile/${user.id}`).then(r => setProfile(r.data)).catch(() => setProfile(false)); };
  useEffect(loadP, [user]);
  const status = (profile && profile.connect_onboarding) || "Not started";
  const complete = status === "Complete";
  const onboard = async () => {
    try { const { data } = await api.post("/store/sellers/connect-onboard", {});
      toast.success("Stripe Connect onboarding started (SANDBOX)");
      if (data?.onboarding_url) window.open(data.onboarding_url, "_blank"); loadP(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const markComplete = async () => {
    try { await api.post("/store/sellers/connect-complete", {}); toast.success("Connect onboarding complete (SANDBOX)"); loadP(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const createListing = async () => {
    try { await api.post("/store/listings", form); toast.success("Listing created"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="k-container" data-testid="connect-onboarding" style={{ padding: 18, marginBottom: 16 }}>
        <h3 className="k-heading" style={{ marginTop: 0, fontSize: 17 }}>Stripe Connect onboarding</h3>
        <p className="k-muted" style={{ fontSize: 13 }}>
          Status: <b data-testid="connect-status">{status}</b>. Identity, bank and KYC are handled by Stripe's hosted flow —
          Kadana never collects them and never holds your share. You must read <b>Complete</b> before listing a paid template or accepting a hire.
        </p>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="k-btn k-btn-primary" data-testid="connect-onboard" onClick={onboard}>Start onboarding</button>
          {!complete && <button className="k-btn k-btn-ghost" data-testid="connect-complete" onClick={markComplete}>Simulate complete (SANDBOX)</button>}
        </div>
      </div>

      <div className="k-container" data-testid="create-listing" style={{ padding: 18 }}>
        <h3 className="k-heading" style={{ marginTop: 0, fontSize: 17 }}>Create a listing</h3>
        {!complete && <div className="k-tag-warn" style={{ marginBottom: 10 }}>Complete Connect onboarding to publish a paid listing.</div>}
        <div style={{ display: "grid", gap: 10 }}>
          <input className="k-control" data-testid="listing-title" placeholder="Title"
            value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <input className="k-control" data-testid="listing-summary" placeholder="Short summary"
            value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="k-control" data-testid="listing-sector" placeholder="Sector (e.g. FIN)" style={{ maxWidth: 150 }}
              value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value.toUpperCase() })} />
            <input className="k-control" data-testid="listing-jur" placeholder="Region (e.g. UK)" style={{ maxWidth: 150 }}
              value={form.primary_jurisdiction} onChange={e => setForm({ ...form, primary_jurisdiction: e.target.value.toUpperCase() })} />
            <select className="k-control" data-testid="listing-license" style={{ maxWidth: 220 }}
              value={form.licensing_type} onChange={e => setForm({ ...form, licensing_type: e.target.value })}>
              <option value="ONE_TIME_PURCHASE">One-time purchase</option>
              <option value="MONTHLY_SUBSCRIPTION">Monthly subscription</option>
              <option value="FREE_OPEN_SOURCE">Free / open source</option>
              <option value="ENTERPRISE_PRIVATE">Enterprise private</option>
            </select>
            <input className="k-control" data-testid="listing-price" type="number" placeholder="price (minor)" style={{ maxWidth: 150 }}
              value={form.price_minor} onChange={e => setForm({ ...form, price_minor: Number(e.target.value) })} />
          </div>
          <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>Fork behaviour: FULL_SOURCE_EXPORT (locked runtime unlocks once managed hosting is approved).</p>
          <button className="k-btn k-btn-primary" data-testid="listing-create" onClick={createListing}>Create listing</button>
        </div>
      </div>
    </div>
  );
}

export default function Marketplace() {
  const [tab, setTab] = useState("explore");
  return (
    <Shell active="marketplace">
      <h1 className="k-heading" style={{ fontSize: 30, margin: 0 }}>Marketplace</h1>
      <p className="k-muted" style={{ marginTop: 6 }}></p>
      <div style={{ display: "flex", gap: 8, margin: "16px 0", flexWrap: "wrap" }}>
        {TABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`tab-${id}`} className={`k-btn ${tab === id ? "k-btn-primary" : "k-btn-ghost"}`}
            onClick={() => setTab(id)}><Icon size={15} /> {label}</button>
        ))}
      </div>
      {tab === "explore" && <ExploreView />}
      {tab === "directory" && <DirectoryView />}
      {tab === "sell" && <SellView />}
      {tab === "creator" && <CreatorDashboard />}
      {tab === "sandbox" && <RegulatorySandbox />}
    </Shell>
  );
}
